// FaceCapture — phase in the J&J clock-in flow that handles:
//   1) Loading face-api.js + 3 models (TinyFaceDetector + landmarks + recognition)
//      from CDN exactly once per session (~6 MB, cached by browser thereafter).
//   2) Streaming the front camera into a <video>.
//   3) Continuously detecting one face + 68 landmarks + 128-d embedding.
//   4) Anti-spoof liveness check via blink detection. We compute the
//      Eye-Aspect-Ratio (EAR) on the 6-point eye landmarks each frame;
//      a real blink is EAR < 0.21 followed by EAR > 0.27 within ~10 frames.
//   5) Captures the embedding ONCE the user has blinked at least once AND
//      the detector score is high; emits via `onComplete(embedding)`.
//   6) If anything fails (no camera, no face after 20s, etc.) it surfaces
//      a "Use QR fallback" button — the parent decides whether the worker
//      has fallback quota remaining and acts accordingly.
//
// face-api.js fork used: @vladmandic/face-api@1.7.13 (maintained, ES2017).

import React, { useEffect, useRef, useState, useCallback } from "react";
import { AlertTriangle, RefreshCw, Camera, CheckCircle2 } from "lucide-react";

const FACE_API_LIB = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/dist/face-api.js";
const FACE_API_MODELS = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/model";

// EAR thresholds tuned for ~30fps mobile front cameras. Tighter would
// reject real blinks; looser would accept a static photo. These let a
// real blink register reliably while a still photo can't fake them.
const EAR_CLOSED = 0.21;
const EAR_OPEN = 0.27;
const MIN_DETECTION_SCORE = 0.5;
const CAPTURE_TIMEOUT_MS = 25_000;

// One-shot module loader. We attach the promise to window so multiple
// FaceCapture mounts in the same SPA session reuse it.
function loadFaceApi() {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (window.__faceApiPromise) return window.__faceApiPromise;
  window.__faceApiPromise = new Promise((resolve, reject) => {
    if (window.faceapi) { resolve(window.faceapi); return; }
    const s = document.createElement("script");
    s.src = FACE_API_LIB;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.onload = async () => {
      try {
        const fa = window.faceapi;
        // tfjs needs an explicit backend init in some environments
        // (notably headless Chrome). Prefer webgl, fall back to cpu.
        try {
          if (fa.tf) {
            await fa.tf.setBackend("webgl");
            await fa.tf.ready();
          }
        } catch {
          try {
            if (fa.tf) {
              await fa.tf.setBackend("cpu");
              await fa.tf.ready();
            }
          } catch { /* let face-api use its default */ }
        }
        await fa.nets.tinyFaceDetector.loadFromUri(FACE_API_MODELS);
        await fa.nets.faceLandmark68Net.loadFromUri(FACE_API_MODELS);
        await fa.nets.faceRecognitionNet.loadFromUri(FACE_API_MODELS);
        resolve(fa);
      } catch (e) { reject(e); }
    };
    s.onerror = () => reject(new Error("Failed to load face-api.js"));
    document.head.appendChild(s);
  });
  return window.__faceApiPromise;
}

// Compute Eye Aspect Ratio from 6 landmark points.
function eyeAspectRatio(pts) {
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  return (dist(pts[1], pts[5]) + dist(pts[2], pts[4])) / (2 * dist(pts[0], pts[3]));
}

export default function FaceCapture({
  mode,                 // "enrol" | "verify"
  workerName,
  fallbacksRemaining,   // null when enrolling
  onComplete,           // ({ embedding, blinks }) => void
  onUseFallback,        // () => void  — called if user clicks "Use QR fallback"
  onCancel,             // () => void
}) {
  const videoRef = useRef(null);
  const detectorLoopRef = useRef(null);
  const timeoutRef = useRef(null);

  // 'loading' = lib loading. 'ready' = camera streaming. 'capturing' =
  // we have a face, waiting for blink. 'captured' = embedding sent up.
  // 'error' = camera denied / model load failed / detector timed out.
  const [phase, setPhase] = useState("loading");
  const [err, setErr] = useState("");
  const [blinkCount, setBlinkCount] = useState(0);
  const blinkStateRef = useRef("open"); // open|closed — for hysteresis
  const blinkCountRef = useRef(0);

  // ---- Tear down camera + detector + script timers cleanly ----
  const teardown = useCallback(() => {
    if (detectorLoopRef.current) {
      cancelAnimationFrame(detectorLoopRef.current);
      detectorLoopRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const stream = videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  // ---- Boot the camera + face-api models ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fa = await loadFaceApi();
        if (cancelled) return;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: 480, height: 480 },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play().catch(() => {});
        setPhase("capturing");

        // Detector loop — runs ~10 fps to stay easy on phone CPU
        const detectorOptions = new fa.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 });
        let lastTick = 0;
        const loop = async (now) => {
          if (cancelled || !videoRef.current) return;
          if (now - lastTick < 100) {
            detectorLoopRef.current = requestAnimationFrame(loop);
            return;
          }
          lastTick = now;
          try {
            const det = await fa.detectSingleFace(videoRef.current, detectorOptions)
              .withFaceLandmarks()
              .withFaceDescriptor();
            if (det && det.detection.score >= MIN_DETECTION_SCORE) {
              // Eye landmarks: 36-41 left, 42-47 right (face-api 68-point map)
              const ll = det.landmarks.getLeftEye();
              const rl = det.landmarks.getRightEye();
              const ear = (eyeAspectRatio(ll) + eyeAspectRatio(rl)) / 2;
              // Blink hysteresis — only count once per close→open transition
              if (blinkStateRef.current === "open" && ear < EAR_CLOSED) {
                blinkStateRef.current = "closed";
              } else if (blinkStateRef.current === "closed" && ear > EAR_OPEN) {
                blinkStateRef.current = "open";
                blinkCountRef.current += 1;
                setBlinkCount(blinkCountRef.current);
              }
              // Capture: need 1 blink + a stable face. Use Array.from to clone
              // the Float32Array so it serialises cleanly to JSON.
              if (blinkCountRef.current >= 1) {
                const embedding = Array.from(det.descriptor);
                cancelled = true;
                setPhase("captured");
                teardown();
                onComplete({ embedding, blinks: blinkCountRef.current });
                return;
              }
            }
          } catch (e) {
            // detect transient errors swallowed; next tick retries
          }
          detectorLoopRef.current = requestAnimationFrame(loop);
        };
        detectorLoopRef.current = requestAnimationFrame(loop);

        // Hard timeout — if no face captured in 25s, escalate to fallback
        timeoutRef.current = setTimeout(() => {
          if (cancelled) return;
          setErr("Couldn't see your face clearly. Move into good light, then try again.");
          setPhase("error");
          teardown();
        }, CAPTURE_TIMEOUT_MS);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.name === "NotAllowedError"
          ? "Camera blocked. Tap 🔒 in your browser bar → Allow camera, then reload."
          : e?.name === "NotFoundError"
            ? "No camera on this device."
            : `Camera error: ${e?.message || e}`;
        setErr(msg);
        setPhase("error");
      }
    })();
    return () => { cancelled = true; teardown(); };
  }, [onComplete, teardown]);

  const isEnrol = mode === "enrol";
  const titleLine = isEnrol
    ? `Save ${workerName}'s face as the baseline`
    : `Verify ${workerName}'s face`;
  const subLine = isEnrol
    ? "Hold the phone steady. Blink once when you see the camera light up."
    : "Look at the camera and blink once.";

  return (
    <div className="px-5 py-5 flex flex-col items-center text-center">
      <div className="text-xs uppercase tracking-wider text-slate-500">
        {isEnrol ? "Face enrolment" : "Face check"}
      </div>
      <h2 className="text-lg font-semibold mt-1">{titleLine}</h2>
      <p className="text-xs text-slate-400 mt-1 max-w-xs">{subLine}</p>

      {/* Camera viewport (circular mask) */}
      <div className="relative my-4 w-56 h-56 rounded-full overflow-hidden border-4 border-slate-700"
           data-testid="face-camera-frame">
        <video ref={videoRef} muted playsInline
               className="w-full h-full object-cover"
               style={{ transform: "scaleX(-1)" /* mirror */ }} />
        {phase === "loading" && (
          <div className="absolute inset-0 bg-slate-900/70 flex items-center justify-center text-xs text-slate-300">
            Loading camera & models…
          </div>
        )}
        {phase === "captured" && (
          <div className="absolute inset-0 bg-emerald-500/30 flex items-center justify-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-300" />
          </div>
        )}
      </div>

      {/* Blink counter */}
      {phase === "capturing" && (
        <div className="text-xs text-slate-400 num"
             data-testid="face-blink-status">
          {blinkCount === 0
            ? "Waiting for you to blink…"
            : "✓ Blink detected — capturing…"}
        </div>
      )}

      {/* Error & fallback */}
      {phase === "error" && (
        <div className="mt-3 w-full max-w-xs space-y-2">
          <div className="flex items-start gap-2 text-left text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{err}</span>
          </div>
          <button
            onClick={() => {
              // re-mount by triggering parent (simpler than internal reset)
              window.location.reload();
            }}
            className="w-full py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm flex items-center justify-center gap-1.5"
            data-testid="face-retry"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Try again
          </button>
          {!isEnrol && fallbacksRemaining > 0 && (
            <button
              onClick={onUseFallback}
              className="w-full py-2.5 rounded bg-amber-500 hover:bg-amber-400 text-amber-950 text-sm font-medium"
              data-testid="face-fallback"
            >
              <Camera className="w-4 h-4 inline mr-1" />
              Skip face check ({fallbacksRemaining} left this month)
            </button>
          )}
          {!isEnrol && fallbacksRemaining === 0 && (
            <div className="text-[11px] text-red-400 leading-relaxed">
              No fallback scans remaining this month. Please ask your supervisor
              to reset your face enrolment.
            </div>
          )}
        </div>
      )}

      {/* Always-on cancel */}
      {phase !== "captured" && (
        <button onClick={onCancel}
          className="mt-5 text-xs text-slate-500 hover:text-slate-300 underline-offset-2 hover:underline"
          data-testid="face-cancel">
          ← Cancel
        </button>
      )}
    </div>
  );
}
